import { useCallback, useEffect, useState } from "react";
import type {
  WellnessProgramDto,
  WellnessProgramStatus,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  activateWellnessProgram,
  archiveWellnessProgram,
  listWellnessPrograms,
} from "@/lib/wellness-program-api";
import { useReadOnly, useSupportView } from "./SupportView";

// TES-294 (J12) — the per-patient versioned Wellness Program on the clinician
// patient page. Read-mostly: lists every version (newest first) with the active
// one highlighted, plus activate (draft → active, supersedes the prior active)
// and archive actions. The full authoring UI is J8/J9 (out of scope here), so
// there is no create form — drafts are authored there and promoted here.

const STATUS_LABELS: Record<WellnessProgramStatus, string> = {
  draft: "Utkast",
  active: "Aktiv",
  replaced: "Ersatt",
  archived: "Arkiverad",
};

const STATUS_CLASSES: Record<WellnessProgramStatus, string> = {
  draft: "bg-surfaceMuted text-textSecondary",
  active: "bg-brand/15 text-brand",
  replaced: "bg-warning/15 text-warning",
  archived: "bg-surfaceMuted text-textSecondary",
};

const BASED_ON_LABELS: Record<string, string> = {
  health_profile: "Hälsoprofil",
  check_in: "Incheckning",
  doctor_restriction: "Läkarrestriktion",
  coach_decision: "Coachbeslut",
};

export function PatientWellnessProgram({ patientId }: { patientId: string }) {
  const { claims, signOut } = useAuth();
  // TES-688 — the coach may now READ this panel (she is the one it is for),
  // but activating and archiving stay with the doctor until Ö-58 says
  // otherwise. Hiding the controls matters: widening only the GETs would
  // otherwise trade a 403 on load for a 403 on click, which is worse — the
  // button would look available and fail.
  // TES-1040 — and never in the admin's read-only view.
  const readOnly = useReadOnly();
  const canManage = claims?.role !== "coach" && !readOnly;
  // TES-1333 — in the coach's view (hers, or the admin's coach support view)
  // a row leads with the patient's goal, the concrete thing the program is
  // about; version and status follow as small metadata. The doctor, who
  // activates and archives versions, keeps the version first.
  const coachView = (useSupportView()?.viewAs ?? claims?.role) === "coach";
  const [programs, setPrograms] = useState<WellnessProgramDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const reload = useCallback(() => {
    listWellnessPrograms(patientId).then(setPrograms).catch(handleError);
  }, [patientId, handleError]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onActivate(programId: string) {
    setBusy(true);
    setError(null);
    try {
      await activateWellnessProgram(patientId, programId);
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function onArchive(programId: string) {
    setBusy(true);
    setError(null);
    try {
      await archiveWellnessProgram(patientId, programId);
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  const active = programs?.find((p) => p.status === "active") ?? null;

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg">Wellnessprogram</h2>
        {/* The coach's rows already badge the active version. A-01: "Statusen får inte upprepas under flera rubriker när den inte förändrar coachens beslut." */}
        {active && !coachView ? (
          <span className="text-xs text-textSecondary">
            Aktiv version: v{active.version}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}

      {!programs ? (
        <p className="text-textSecondary text-sm">Hämtar…</p>
      ) : programs.length === 0 ? (
        <p className="text-textSecondary text-sm">
          Inget wellnessprogram ännu. Utkast skapas i redigeringsvyn (J8/J9).
        </p>
      ) : (
        <div className="flex flex-col gap-xs">
          {programs.map((p) => (
            <div
              key={p.id}
              className="flex items-start justify-between border-b border-surfaceMuted last:border-b-0 py-sm text-sm gap-sm"
            >
              {coachView ? (
                <CoachProgramRow program={p} />
              ) : (
                <div className="flex flex-col gap-[2px] min-w-0">
                  <span className="flex items-center gap-sm">
                    <span className="font-medium">v{p.version}</span>
                    <span
                      className={`inline-block rounded-full text-[11px] font-semibold px-sm py-[1px] ${STATUS_CLASSES[p.status]}`}
                    >
                      {STATUS_LABELS[p.status]}
                    </span>
                    <span className="text-textSecondary text-xs">
                      {BASED_ON_LABELS[p.basedOn] ?? p.basedOn}
                    </span>
                  </span>
                  {p.patientGoal ? (
                    <span className="text-xs text-textSecondary truncate">
                      Mål: {p.patientGoal.sv}
                    </span>
                  ) : null}
                  <span className="text-xs text-textSecondary">
                    {p.modules.length}{" "}
                    {p.modules.length === 1 ? "modul" : "moduler"}
                    {p.status === "replaced" && p.replacedByVersion
                      ? ` • ersatt av v${p.replacedByVersion}`
                      : null}
                  </span>
                  {p.recommendationNote ? (
                    <span className="text-xs text-warning truncate">
                      Rekommendation: {p.recommendationNote}
                    </span>
                  ) : null}
                </div>
              )}
              <div className="flex items-center gap-sm shrink-0">
                {canManage && p.status === "draft" ? (
                  <button
                    type="button"
                    onClick={() => onActivate(p.id)}
                    disabled={busy}
                    className="text-xs text-brand hover:opacity-80 disabled:opacity-50"
                  >
                    Aktivera
                  </button>
                ) : null}
                {canManage &&
                (p.status === "draft" || p.status === "active") ? (
                  <button
                    type="button"
                    onClick={() => onArchive(p.id)}
                    disabled={busy}
                    className="text-xs text-textSecondary hover:text-danger disabled:opacity-50"
                  >
                    Arkivera
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// TES-1333 — the coach's row: the goal (what the program is for) first, then
// version, basis and modules with the status as a small badge. Without a
// goal the version is the only identity the row has, so it leads.
function CoachProgramRow({ program: p }: { program: WellnessProgramDto }) {
  const meta = [
    `v${p.version}`,
    BASED_ON_LABELS[p.basedOn] ?? p.basedOn,
    `${p.modules.length} ${p.modules.length === 1 ? "modul" : "moduler"}`,
    p.status === "replaced" && p.replacedByVersion
      ? `ersatt av v${p.replacedByVersion}`
      : null,
  ].filter(Boolean);
  return (
    <div className="flex flex-col gap-[2px] min-w-0">
      <span className="text-textPrimary">
        {p.patientGoal ? `Mål: ${p.patientGoal.sv}` : `Version ${p.version}`}
      </span>
      {p.recommendationNote ? (
        <span className="text-xs text-warning">
          Rekommendation: {p.recommendationNote}
        </span>
      ) : null}
      <span className="flex flex-wrap items-center gap-xs text-xs text-textSecondary">
        {meta.join(" · ")}
        <span
          className={`inline-block rounded-full text-[11px] px-sm py-[1px] ${STATUS_CLASSES[p.status]}`}
        >
          {STATUS_LABELS[p.status]}
        </span>
      </span>
    </div>
  );
}
